/**
 * =============================================================================
 * customScrollbar - Scrollbar DOM skinable (arte de `assets/ui/scrollbar/`)
 * =============================================================================
 *
 * Reemplaza el scrollbar nativo de un contenedor scrolleable por un overlay
 * dibujado con el arte dorado de Aureum: flecha-arriba, track, thumb y
 * flecha-abajo (todos con estado HOVER). El contenido sigue scrolleando nativo
 * (rueda, teclado, drag del thumb, click en flechas con auto-repeat).
 *
 * REGLA: para cualquier contenedor `overflow:auto/scroll` DOM nuevo, usar
 * `attachCustomScrollbar(el)` en vez de estilizar `::-webkit-scrollbar` a mano.
 * Ver docs/UI_SKINNING.md §"Scrollbar custom (DOM)".
 *
 * Para inputs/selects/checkboxes ver `formControls.ts`.
 */

import { getAssetUrl } from '@shared/constants';

const TRACK_URL = getAssetUrl('ui/scrollbar/scroll_bar.png'); // 33x1218
const THUMB_URL = getAssetUrl('ui/scrollbar/scroll_handler.png'); // 28x73
const THUMB_HOVER_URL = getAssetUrl('ui/scrollbar/scroll_handler-HOVER.png');
const TOP_URL = getAssetUrl('ui/scrollbar/scroll_top_arrow.png'); // 28x28
const TOP_HOVER_URL = getAssetUrl('ui/scrollbar/scroll_top_arrow-HOVER.png');
const BOT_URL = getAssetUrl('ui/scrollbar/scroll_bot_arrow.png'); // 28x28
const BOT_HOVER_URL = getAssetUrl('ui/scrollbar/scroll_bot_arrow-HOVER.png');

/** Ancho (px) de la columna del scrollbar. */
const SB_WIDTH = 16;
/** Alto (px) de cada botón de flecha (cuadrado). */
const ARROW_SIZE = SB_WIDTH;
/** Alto mínimo (px) del thumb para que se vean sus endcaps. */
const MIN_THUMB = 26;
/** Cuánto scrollea cada click/auto-repeat de flecha (px). */
const ARROW_STEP = 40;

let styleInjected = false;

function ensureScrollbarStyle(): void {
  if (styleInjected || document.getElementById('aureum-scrollbar-style')) {
    styleInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'aureum-scrollbar-style';
  style.textContent = `
    .aureum-scroll-host { scrollbar-width: none; -ms-overflow-style: none; }
    .aureum-scroll-host::-webkit-scrollbar { width: 0; height: 0; display: none; }

    .aureum-scrollbar {
      position: absolute;
      top: 0;
      right: 0;
      width: ${SB_WIDTH}px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      z-index: 5;
      user-select: none;
    }
    .aureum-scrollbar__arrow {
      flex: 0 0 auto;
      height: ${ARROW_SIZE}px;
      background-size: 100% 100%;
      background-repeat: no-repeat;
      cursor: pointer;
    }
    .aureum-scrollbar__arrow--top { background-image: url("${TOP_URL}"); }
    .aureum-scrollbar__arrow--top:hover { background-image: url("${TOP_HOVER_URL}"); }
    .aureum-scrollbar__arrow--bot { background-image: url("${BOT_URL}"); }
    .aureum-scrollbar__arrow--bot:hover { background-image: url("${BOT_HOVER_URL}"); }

    .aureum-scrollbar__track {
      position: relative;
      flex: 1 1 auto;
      background-image: url("${TRACK_URL}");
      background-size: 100% 100%;
      background-repeat: no-repeat;
      min-height: 0;
    }
    .aureum-scrollbar__thumb {
      position: absolute;
      left: 0;
      width: 100%;
      background-image: url("${THUMB_URL}");
      background-size: 100% 100%;
      background-repeat: no-repeat;
      cursor: grab;
    }
    .aureum-scrollbar__thumb:hover,
    .aureum-scrollbar__thumb.aureum-dragging {
      background-image: url("${THUMB_HOVER_URL}");
    }
    .aureum-scrollbar__thumb.aureum-dragging { cursor: grabbing; }
  `;
  document.head.appendChild(style);
  styleInjected = true;
}

export interface CustomScrollbar {
  /** Recalcula tamaño/posición del thumb (llamar tras cambiar el contenido). */
  refresh: () => void;
  /** Quita el overlay y restaura el contenedor. */
  destroy: () => void;
}

/**
 * Igual que `attachCustomScrollbar` pero DIFIERE el montaje a
 * `requestAnimationFrame`, para poder llamarlo apenas se setea `overflow` sobre
 * un elemento que todavía NO fue insertado en el DOM (patrón típico: crear →
 * estilar → append más abajo en la misma función). Devuelve un handle estable
 * que delega al real una vez montado.
 */
export function skinScrollable(scrollEl: HTMLElement): CustomScrollbar {
  let real: CustomScrollbar | null = null;
  let destroyed = false;
  requestAnimationFrame(() => {
    if (destroyed) return;
    real = attachCustomScrollbar(scrollEl);
  });
  return {
    refresh: () => real?.refresh(),
    destroy: () => {
      destroyed = true;
      real?.destroy();
    },
  };
}

/**
 * Acopla un scrollbar custom a `scrollEl` (un contenedor con `overflow:auto`).
 * El padre posicionado del overlay es `scrollEl.parentElement`; si su `position`
 * es `static`, se le pone `relative` para anclar el overlay.
 */
export function attachCustomScrollbar(scrollEl: HTMLElement): CustomScrollbar {
  ensureScrollbarStyle();

  const host = scrollEl.parentElement;
  if (!host) {
    // Sin padre no hay dónde anclar el overlay; devolver un handle inerte.
    return { refresh: () => {}, destroy: () => {} };
  }
  if (getComputedStyle(host).position === 'static') {
    host.style.position = 'relative';
  }

  scrollEl.classList.add('aureum-scroll-host');
  // Reservar lugar para la columna del scrollbar.
  const prevPaddingRight = scrollEl.style.paddingRight;
  scrollEl.style.paddingRight = `${SB_WIDTH + 4}px`;

  const bar = document.createElement('div');
  bar.className = 'aureum-scrollbar';

  const topArrow = document.createElement('div');
  topArrow.className = 'aureum-scrollbar__arrow aureum-scrollbar__arrow--top';

  const track = document.createElement('div');
  track.className = 'aureum-scrollbar__track';

  const thumb = document.createElement('div');
  thumb.className = 'aureum-scrollbar__thumb';
  track.appendChild(thumb);

  const botArrow = document.createElement('div');
  botArrow.className = 'aureum-scrollbar__arrow aureum-scrollbar__arrow--bot';

  bar.appendChild(topArrow);
  bar.appendChild(track);
  bar.appendChild(botArrow);
  host.appendChild(bar);

  // --- Sync overlay con el área scrolleable -------------------------------
  let trackH = 0;

  const refresh = (): void => {
    // Posicionar la barra sobre el viewport del contenedor.
    bar.style.top = `${scrollEl.offsetTop}px`;
    bar.style.right = `${host.clientWidth - (scrollEl.offsetLeft + scrollEl.offsetWidth)}px`;
    bar.style.height = `${scrollEl.clientHeight}px`;

    const { scrollHeight, clientHeight, scrollTop } = scrollEl;
    const scrollable = scrollHeight - clientHeight;
    // Sin contenido para scrollear: ocultar la barra.
    if (scrollable <= 1) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = 'flex';

    trackH = track.clientHeight;
    const thumbH = Math.max(MIN_THUMB, (clientHeight / scrollHeight) * trackH);
    const maxThumbTop = trackH - thumbH;
    const thumbTop = maxThumbTop * (scrollTop / scrollable);

    thumb.style.height = `${thumbH}px`;
    thumb.style.top = `${thumbTop}px`;
  };

  const onScroll = (): void => refresh();
  scrollEl.addEventListener('scroll', onScroll, { passive: true });

  const ro = new ResizeObserver(() => refresh());
  ro.observe(scrollEl);

  // El ResizeObserver sólo dispara cuando cambia el box del propio contenedor;
  // si el contenido crece dentro de un alto fijo (maxHeight), el scrollHeight
  // cambia sin redimensionar el contenedor. Observamos también el subárbol.
  const mo = new MutationObserver(() => refresh());
  mo.observe(scrollEl, { childList: true, subtree: true, characterData: true });

  // --- Drag del thumb ------------------------------------------------------
  let dragStartY = 0;
  let dragStartScroll = 0;

  const onThumbMove = (e: PointerEvent): void => {
    const thumbH = thumb.offsetHeight;
    const maxThumbTop = trackH - thumbH;
    if (maxThumbTop <= 0) return;
    const scrollable = scrollEl.scrollHeight - scrollEl.clientHeight;
    const deltaY = e.clientY - dragStartY;
    const deltaScroll = (deltaY / maxThumbTop) * scrollable;
    scrollEl.scrollTop = dragStartScroll + deltaScroll;
  };
  const onThumbUp = (e: PointerEvent): void => {
    thumb.classList.remove('aureum-dragging');
    thumb.releasePointerCapture(e.pointerId);
    window.removeEventListener('pointermove', onThumbMove);
    window.removeEventListener('pointerup', onThumbUp);
  };
  const onThumbDown = (e: PointerEvent): void => {
    e.preventDefault();
    dragStartY = e.clientY;
    dragStartScroll = scrollEl.scrollTop;
    thumb.classList.add('aureum-dragging');
    thumb.setPointerCapture(e.pointerId);
    window.addEventListener('pointermove', onThumbMove);
    window.addEventListener('pointerup', onThumbUp);
  };
  thumb.addEventListener('pointerdown', onThumbDown);

  // --- Flechas con auto-repeat --------------------------------------------
  let repeatTimer: number | null = null;
  const stopRepeat = (): void => {
    if (repeatTimer !== null) {
      window.clearInterval(repeatTimer);
      repeatTimer = null;
    }
  };
  const startRepeat = (dir: number) => (e: PointerEvent): void => {
    e.preventDefault();
    const step = (): void => {
      scrollEl.scrollTop += dir * ARROW_STEP;
    };
    step();
    stopRepeat();
    repeatTimer = window.setInterval(step, 90);
  };
  const onTopDown = startRepeat(-1);
  const onBotDown = startRepeat(1);
  topArrow.addEventListener('pointerdown', onTopDown);
  botArrow.addEventListener('pointerdown', onBotDown);
  window.addEventListener('pointerup', stopRepeat);

  // --- Click en el track (page up/down) -----------------------------------
  const onTrackDown = (e: PointerEvent): void => {
    if (e.target === thumb) return;
    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const dir = clickY < thumb.offsetTop ? -1 : 1;
    scrollEl.scrollTop += dir * scrollEl.clientHeight * 0.9;
  };
  track.addEventListener('pointerdown', onTrackDown);

  refresh();

  const destroy = (): void => {
    stopRepeat();
    scrollEl.removeEventListener('scroll', onScroll);
    ro.disconnect();
    mo.disconnect();
    thumb.removeEventListener('pointerdown', onThumbDown);
    topArrow.removeEventListener('pointerdown', onTopDown);
    botArrow.removeEventListener('pointerdown', onBotDown);
    track.removeEventListener('pointerdown', onTrackDown);
    window.removeEventListener('pointermove', onThumbMove);
    window.removeEventListener('pointerup', onThumbUp);
    window.removeEventListener('pointerup', stopRepeat);
    bar.remove();
    scrollEl.classList.remove('aureum-scroll-host');
    scrollEl.style.paddingRight = prevPaddingRight;
  };

  return { refresh, destroy };
}
